import React from 'react';
import { Leaderboard } from '@mentimeter/visualizations';
import type { Player } from '@mentimeter/visualizations';
import { Text } from '@mentimeter/ragnar-ui/text';
import { createQuizSvgMapInDOM, shortnameMap } from '@mentimeter/quiz-emojis';
import type { QuizPlayerScore } from '@mentimeter/quiz-types';
import { useRagnar } from '@mentimeter/ragnar-react';
import { useTranslations } from 'next-intl';

export const NoResults = () => {
  const translate = useTranslations('presenting.legacy');

  return (
    <>
      <Text
        data-testid="leaderboard-no-results"
        fontWeight="semiBold"
        color="text"
        fontSize="400"
      >
        {translate('quiz_results.no_results_yet')}
      </Text>
      <Text fontSize="250">{translate('quiz_results.top_participants')}</Text>
    </>
  );
};

export const QuizLeaderboardVisualization = ({
  results,
  enableAnimations,
  timeBasedScoring,
  revealWinner,
  id,
  onPlayerNameClick,
}: {
  results: QuizPlayerScore[];
  timeBasedScoring: boolean;
  enableAnimations: boolean;
  revealWinner: boolean;
  id: string;
  onPlayerNameClick?: (payload: { identifier: string; name: string }) => void;
}) => {
  const {
    theme: { colors, visualizationColors },
  } = useRagnar();
  React.useEffect(() => {
    createQuizSvgMapInDOM();
  }, []);

  const t = useTranslations('presenting.quiz.leaderboard');

  const players = React.useMemo(() => {
    return results.map<Player>((player): Player => {
      return {
        choiceId: player.score?.question?.choiceId || 0,
        correctAnswer: !!player.score?.question?.correctAnswer,
        correct_answer: !!player.score?.question?.correctAnswer,
        points: [],
        totalScore: player.score?.total.totalScore,
        position: player.score?.total.position,
        questionScore: player.score?.question?.score || 0,
        score: player.score?.question?.score || 0,
        question: {
          score: player.score?.question?.score ?? 0,
          correctAnswer: player.score?.question?.correctAnswer || false,
          choiceId: player.score?.question?.choiceId ?? 0,
        },
        ...player.player,
        name: player.player.name ?? player.player.emojiName,
        emojiURL: `#emoji-${
          shortnameMap[
            player.player.emojiShortname as keyof typeof shortnameMap
          ]
        }`,
      };
    });
  }, [results]);
  const selector = `leaderboard-${id}`;

  const leaderboardRef = React.useRef<Leaderboard | undefined>(undefined);
  React.useEffect(() => {
    if (leaderboardRef.current) {
      return;
    }
    leaderboardRef.current = new Leaderboard({
      selector: `#${selector}`,
      players,
      bg: colors.bg,
      textColor: colors.text,
      playerColors: visualizationColors.fillColors,
      animationMultiplier: enableAnimations ? 1 : 0,
      timeBasedScoring,
      showQuizWinner: revealWinner,
      removePlayerName: onPlayerNameClick,
      fastestLabel: t('fastest_player_label'),
    });
    leaderboardRef.current.update();
  }, [
    selector,
    colors.bg,
    colors.text,
    enableAnimations,
    players,
    revealWinner,
    timeBasedScoring,
    visualizationColors.fillColors,
    onPlayerNameClick,
    t,
  ]);

  React.useEffect(() => {
    return () => {
      if (leaderboardRef.current) leaderboardRef.current.cleanup();
    };
  }, []);

  return (
    <svg
      data-testid="leaderboard-results"
      width="100%"
      height="100%"
      viewBox="0 0 960 540"
      preserveAspectRatio="xMidYMid"
      style={{ overflow: 'visible' }}
    >
      <g id={selector} />
    </svg>
  );
};
